'use client'

import { useEffect, useRef, useState } from 'react'

import TestChatTipCloseIcon from '@/components/svg/TestChatTipCloseIcon'
import useI18n from 'Hooks/useI18n'

interface TestChatLocalTooltipProps {
  visible: boolean
  onClose?: () => void
  outsideClosable?: boolean
  content: React.ReactNode
  children: React.ReactNode
  width?: number
  alignRightEdgeToTrigger?: boolean
  horizontalOffset?: number
  arrowRightOffset?: number
  closeIcon?: React.ReactNode
  zIndex?: number
  triggerGap?: number
}

export default function TestChatLocalTooltip({
  visible,
  onClose,
  outsideClosable = false,
  content,
  children,
  width = 260,
  alignRightEdgeToTrigger = false,
  horizontalOffset = 0,
  arrowRightOffset,
  closeIcon,
  zIndex = 1200,
  triggerGap = 4,
}: TestChatLocalTooltipProps) {
  const triggerRef = useRef<HTMLSpanElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [triggerWidth, setTriggerWidth] = useState(0)
  const { t } = useI18n()

  useEffect(() => {
    const trigger = triggerRef.current
    if (!trigger) return

    setTriggerWidth(trigger.getBoundingClientRect().width)
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      setTriggerWidth(entry.contentRect.width)
    })
    observer.observe(trigger)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!visible || !outsideClosable) return

    const handleOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      const inTooltip = tooltipRef.current?.contains(target)
      const inTrigger = triggerRef.current?.contains(target)
      if (inTooltip || inTrigger) return
      onClose?.()
    }

    window.addEventListener('pointerdown', handleOutsidePointerDown, true)
    return () =>
      window.removeEventListener('pointerdown', handleOutsidePointerDown, true)
  }, [outsideClosable, onClose, visible])

  const canShowBubble = visible

  return (
    <>
      <span
        ref={triggerRef}
        className="relative inline-flex align-middle leading-none"
      >
        {children}

        {canShowBubble && (
          <div
            ref={tooltipRef}
            className="pointer-events-auto absolute"
            style={{
              width,
              zIndex,
              bottom: `calc(100% + ${triggerGap}px)`,
              ...(alignRightEdgeToTrigger
                ? { right: -horizontalOffset }
                : {
                    left: `calc(50% + ${horizontalOffset}px)`,
                    transform: 'translateX(-50%)',
                  }),
            }}
          >
            <div className="relative rounded-[12px] bg-[rgba(0,0,0,0.85)] p-3 pl-4 pr-8 text-[11px] leading-4 text-white">
              <button
                type="button"
                className="absolute right-1 top-1 text-white/70 hover:text-white"
                aria-label={t('Close')}
                onClick={() => onClose?.()}
              >
                {closeIcon ?? <TestChatTipCloseIcon width={24} height={24} />}
              </button>
              {content}
            </div>
            <div
              className="absolute h-6 w-6 rounded-[4px] bg-[rgba(0,0,0,0.85)]"
              style={{
                left:
                  typeof arrowRightOffset === 'number'
                    ? width - arrowRightOffset - 12
                    : Math.max(
                        8,
                        Math.min(
                          width - 20,
                          width - triggerWidth / 2 - horizontalOffset - 6
                        )
                      ),
                bottom: -2,
                transform: 'rotate(45deg)',
              }}
            />
          </div>
        )}
      </span>
    </>
  )
}
